import {Component, OnInit, OnDestroy} from '@angular/core';
import {BaseComponent} from '../../base/base.component';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {Apollo, QueryRef} from 'apollo-angular';
import {
  AddLikeGQL,
  CreateIdeaCommentGQL,
  GetChallengeGQL,
  GetIdeaGQL, GetIdeaQuery, Idea,
  IdeaKaizen
} from '../../../generated/graphql';

import {GetIdeaFilesGQL, IdeaFileItem} from 'src/generated/graphql/idea/get-idea-files.graphql';
import {GetIdeaKaizenDetailGQL} from 'src/generated/graphql/idea-kaizen/get-idea-kaizen-detail.graphql';
import {resolveAwardImage} from '../clic-store/store.utils';
import {IdeaAttachmentService} from '../../service/idea-attachment.service';
import {MAX_IDEA_ATTACHMENTS} from '../../components/idea-attachments/idea-attachments.constants';

@Component({
  selector: 'app-idea-detail',
  templateUrl: './idea-detail.component.html',
  styleUrls: ['./idea-detail.component.scss']
})
export class IdeaDetailComponent extends BaseComponent implements OnInit, OnDestroy {
  // anexos da ideia tradicional (somente leitura); Kaizen fica fora por ora
  isKaizen = false;
  ideaFiles: IdeaFileItem[] = [];
  // vem do backend (mesma regra do upload/remoção); só mostra/esconde os controles
  canManageFiles = false;
  pendingFiles: File[] = [];
  uploadingFiles = false;


  paramId = null;
  idea: Idea | IdeaKaizen
  ideaKaizen: IdeaKaizen = new IdeaKaizen();
  ideaQuery: QueryRef<GetIdeaQuery>;
  model: any = {};

  constructor(public router: Router,
              public route2: ActivatedRoute,
              private apollo: Apollo,
              private getChallengeGQL: GetChallengeGQL,
              private getIdeaGQL: GetIdeaGQL,
              private getIdeaKaizenDetailGQL: GetIdeaKaizenDetailGQL,
              private getIdeaFilesGQL: GetIdeaFilesGQL,
              private ideaAttachmentService: IdeaAttachmentService,
              private createIdeaCommentGQL: CreateIdeaCommentGQL,
              private addLikeGQL: AddLikeGQL,
              public translate: TranslateService) {
    super(router, translate);
  }

  ngOnInit(): void {
    document.body.classList.add('inside');
    this.route2.params.subscribe(url => {
      if (!this.isNullOrUndefined(url)) {
        this.paramId = url.id;
        this.isKaizen = url.code.includes('K');
        this.ideaFiles = [];
        this.canManageFiles = false;
        this.pendingFiles = [];
        this.isKaizen ? this.getIdeaKaizen(url) : this.getIdea(url)
      }
    });
  }

  ngOnDestroy(): void {
    document.body.classList.remove('inside');
  }

  getIdea(url) {
    this.ideaQuery = this.getIdeaGQL.watch({id: url.id});
    this.ideaQuery.valueChanges.subscribe(({data}) => {
      this.idea = new Idea()
      Object.assign(this.idea, data.getIdea as Idea);
      this.idea.expectations = this.idea.expectations
        .filter(a => this.isNullOrUndefined(a.challengeLanguage)
          || a.challengeLanguage.includes(this.translate.currentLang));
    });
    this.getIdeaFiles(url.id);
  }

  /** Anexos ativos da ideia tradicional, por query dedicada (sem alterar o fragment `Idea`). */
  getIdeaFiles(id: string) {
    this.getIdeaFilesGQL.fetch({id}, {fetchPolicy: 'network-only'}).subscribe({
      next: ({data}) => {
        this.ideaFiles = data?.getIdea?.ideafiles || [];
        this.canManageFiles = !!data?.getIdea?.canManageFiles;
      },
      error: () => {
        this.ideaFiles = [];
        this.canManageFiles = false;
      },
    });
  }

  fileUrl(file: IdeaFileItem): string {
    return resolveAwardImage(file?.fileName);
  }

  /** Vagas restantes: limite do backend menos os anexos ativos (o cartão Kaizen já está na lista). */
  get attachmentSlots(): number {
    return Math.max(0, MAX_IDEA_ATTACHMENTS - (this.ideaFiles?.length || 0));
  }

  async onUploadFiles() {
    if (!this.pendingFiles.length || this.uploadingFiles || !this.paramId) {
      return;
    }
    this.uploadingFiles = true;
    const results = await this.ideaAttachmentService.uploadAll(
      this.isKaizen ? 'kaizen' : 'idea', this.paramId, this.pendingFiles);
    this.pendingFiles = [];
    this.uploadingFiles = false;
    this.reloadFiles();
    const summary = this.ideaAttachmentService.summarize(results);
    if (summary) {
      this.showMessage(this.translate.instant('attachments.summaryTitle'), summary.html, summary.type);
    }
  }

  onRemoveFile(file: IdeaFileItem) {
    this.confirmMessage(this.translate.instant('attention'), this.translate.instant('attachments.confirmRemove'),
      () => this.removeFile(file));
  }

  private async removeFile(file: IdeaFileItem) {
    const result = this.isKaizen
      ? await this.ideaAttachmentService.removeKaizenFile(file.id)
      : await this.ideaAttachmentService.removeIdeaFile(file.id);
    if (result.ok) {
      this.reloadFiles();
      this.showMessage(this.translate.instant('attachments.summaryTitle'),
        this.translate.instant('attachments.removed'), 'success');
    } else {
      this.showMessage(this.translate.instant('attention'), result.message);
    }
  }

  /** Re-busca a lista (e o canManageFiles) depois de adicionar/remover. */
  private reloadFiles() {
    this.isKaizen ? this.getIdeaKaizen({id: this.paramId}) : this.getIdeaFiles(this.paramId);
  }

  // Query escrita à mão: a GetIdeaKaizen gerada usava um fragment inexistente (API 400).
  getIdeaKaizen(url) {
    this.getIdeaKaizenDetailGQL.fetch({id: url.id}, {fetchPolicy: 'network-only'}).subscribe({
      next: ({data}) => {
        const kaizen = data?.getIdeaKaizen;
        this.idea = Object.assign(new IdeaKaizen(), kaizen);
        this.ideaFiles = kaizen?.ideafiles || [];
        this.canManageFiles = !!kaizen?.canManageFiles;
      },
      error: (err) => {
        console.error('Erro ao carregar a ideia Kaizen', err);
        this.ideaFiles = [];
        this.canManageFiles = false;
        const message = err?.graphQLErrors?.[0]?.message || err?.message;
        if (message) {
          this.showMessage(this.translate.instant('attention'), message);
        }
      },
    });
  }

  /** Texto principal: a ideia tradicional usa `description`; o Kaizen guarda o conteúdo em `problem`. */
  get descriptionText(): string {
    const idea: any = this.idea;
    return idea?.description || (this.isKaizen ? idea?.problem : '') || '';
  }

  get solutionText(): string {
    const idea: any = this.idea;
    return this.isKaizen ? idea?.solution || '' : '';
  }

  actionLike() {
    this.addLikeGQL.mutate({id: this.paramId}).subscribe(async ({data}) => {
      this.showMessage(this.translate.instant('success'), this.translate.instant('likeSend'), 'success');
      setTimeout(() => {
        this.ideaQuery.refetch({id: this.paramId});
      });
    });
  }

  onSaveIdeaComment() {
    const arrValidateFields = [
      {value: this.model.name, text: this.translate.instant('ideaDetail.name') + ', <br />'},
      {value: this.model.message, text: this.translate.instant('ideaDetail.message') + '<br />'},
    ];
    const stringError = this.validateField(arrValidateFields);
    if (!super.isNullOrUndefined(stringError)) {
      this.showMessage(this.translate.instant('attention'), this.translate.instant('required') + `<br><br> ${stringError}`);
      return;
    }
    const a: any = {data: this.model, id: this.paramId};
    this.createIdeaCommentGQL.mutate(a).subscribe(
      () => {
        this.model = {};
        this.showMessage(this.translate.instant('success'), this.translate.instant('commentSend'), 'success');
        this.ideaQuery.refetch({id: this.paramId});
        return;
      }, (err) => {
      }
    );
  }

}
